// components/SteinListe.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate, useSearchParams as useReactRouterSearchParams } from 'react-router-dom';
import debounce from 'lodash.debounce';
import API from '../services/api';
import Modal from './Modal';
import SteinBild from './SteinBild';
import '../styles/SteinListe.css';
import '../styles/SearchForm.css';
import '../styles/Sort.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faSortUp, faSortDown, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

const KATEGORIEN = ['Einzelscheiben', 'Serien', 'Längsschnitte', 'Kristalle'];

const FILTER_STORAGE_KEY = 'steinListeFilters';

const getParamsObject = (searchParams) => Object.fromEntries(searchParams.entries());

const defaultParams = {
    search: '', farbe: '', kategorie: '', merkmal: '', serienBezeichnung: '', kennzeichen: '',
    status: '', gewichtMin: '', gewichtMax: '', preisProGrammMin: '', preisProGrammMax: '',
    durchmesserMin: '', durchmesserMax: '', kaufPreisMin: '', kaufPreisMax: '',
    kaufDatumVon: '', kaufDatumBis: '', verkaufPreisMin: '', verkaufPreisMax: '',
    verkaufDatumVon: '', verkaufDatumBis: '', laengeMin: '', laengeMax: '', breiteMin: '', breiteMax: '',
    sortBy: 'name', sortOrder: 'asc', page: '1', limit: '0',
};

function SteinListe() {
    const [steine, setSteine] = useState([]);
    const [totalSteine, setTotalSteine] = useState(0);
    const userRole = localStorage.getItem('role') || 'guest';
    const [isLoading, setIsLoading] = useState(true); // Start loading initially
    const [error, setError] = useState(null);

    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useReactRouterSearchParams();

    // State derived from URL parameters
    const currentParams = useMemo(() => ({
        ...defaultParams,
        ...getParamsObject(searchParams),
    }), [searchParams]);

    const [showModal, setShowModal] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [showFilter, setShowFilter] = useState(false);

    const [farbenOptions, setFarbenOptions] = useState([]);
    const [merkmalOptions, setMerkmalOptions] = useState([]);
    const [serienBezeichnungOptions, setSerienBezeichnungOptions] = useState([]);

    // Ref to track if scroll restoration logic should be active
    const isRestoringScroll = useRef(sessionStorage.getItem('restoreScroll') === 'true');
    // Ref to track previous params to detect actual changes relevant for scrolling top
    const prevParamsRef = useRef(currentParams);
    // Ref to ensure filter restoration only runs once on mount
    const didRestoreFilters = useRef(false);

    // --- Fetching Data ---

    const fetchFilterOptions = useCallback(() => {
        API.get('/steine/farben')
            .then((res) => setFarbenOptions(res.data.filter(Boolean).sort()))
            .catch((err) => console.error('Fehler beim Laden der Farben:', err));
        API.get('/steine/merkmale')
            .then((res) => setMerkmalOptions(res.data.filter(Boolean).sort()))
            .catch((err) => console.error('Fehler beim Laden der Merkmale:', err));
        API.get('/steine/serienBezeichnungen')
            .then((res) => setSerienBezeichnungOptions(res.data.filter(Boolean).sort()))
            .catch((err) => console.error('Fehler beim Laden der Serienbezeichnungen:', err));
    }, []);

    // Fetch Steine based on currentParams (URL)
    const fetchSteine = useCallback((paramsToUse) => {
        setIsLoading(true);
        setError(null);
        const query = new URLSearchParams(paramsToUse).toString();

        API.get(`/steine/search?${query}`)
            .then((res) => {
                setSteine(res.data.steine || []);
                setTotalSteine(res.data.total || 0);
            })
            .catch((err) => {
                console.error("Fehler beim Laden der Steine:", err);
                if (err.response && (err.response.status === 401 || (err.response.status === 400 && err.response.data === 'Ungültiges Token'))) {
                    setError('Sitzung abgelaufen. Bitte neu anmelden.');
                } else {
                    setError('Fehler beim Laden der Steine. Bitte erneut versuchen.');
                }
                setSteine([]);
                setTotalSteine(0);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedFetch = useCallback(debounce((params) => {
        fetchSteine(params);
    }, 350), [fetchSteine]);

    // Effect to fetch data when URL params change
    useEffect(() => {
        isRestoringScroll.current = sessionStorage.getItem('restoreScroll') === 'true';
        debouncedFetch(currentParams);
        return () => {
            debouncedFetch.cancel();
        };
    }, [currentParams, debouncedFetch]);

    // --- Initial Load ---
    useEffect(() => {
        fetchFilterOptions();
    }, [fetchFilterOptions]); // fetchFilterOptions is stable

    // --- Filter-State Session-Persistenz ---
    // Einmalig beim Mount: URL-Parameter aus sessionStorage wiederherstellen,
    // falls die URL keine Filter enthält (z.B. nach navigate('/') aus SteinDetail).
    useEffect(() => {
        if (didRestoreFilters.current) return;
        didRestoreFilters.current = true;
        const stored = sessionStorage.getItem(FILTER_STORAGE_KEY);
        if (stored && !searchParams.toString()) {
            setSearchParams(new URLSearchParams(stored), { replace: true });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Aktuelle URL-Parameter in sessionStorage spiegeln
    useEffect(() => {
        const queryString = searchParams.toString();
        if (queryString) {
            sessionStorage.setItem(FILTER_STORAGE_KEY, queryString);
        }
    }, [searchParams]);

    // --- Scroll Restoration Logic ---
    useEffect(() => {
        if (!isLoading && isRestoringScroll.current) {
            const position = sessionStorage.getItem('scrollPosition');
            const stonesAvailable = Array.isArray(steine) && steine.length > 0;

            if (position != null && stonesAvailable) {
                requestAnimationFrame(() => {
                    window.scrollTo({ top: parseInt(position, 10), behavior: 'instant' });
                    sessionStorage.removeItem('scrollPosition');
                    sessionStorage.removeItem('restoreScroll');
                    isRestoringScroll.current = false;
                });
            } else {
                sessionStorage.removeItem('scrollPosition');
                sessionStorage.removeItem('restoreScroll');
                isRestoringScroll.current = false;
            }
        }
    }, [isLoading, steine]);


    // --- Scroll To Top on Filter/Page/Sort Change ---
    useEffect(() => {
        const paramsJustChanged = prevParamsRef.current !== currentParams;
        const shouldRestore = sessionStorage.getItem('restoreScroll') === 'true';

        const relevantParamsChanged = (
            prevParamsRef.current.page !== currentParams.page ||
            prevParamsRef.current.sortBy !== currentParams.sortBy ||
            prevParamsRef.current.sortOrder !== currentParams.sortOrder ||
            prevParamsRef.current.limit !== currentParams.limit ||
            prevParamsRef.current.search !== currentParams.search ||
            prevParamsRef.current.farbe !== currentParams.farbe ||
            prevParamsRef.current.kategorie !== currentParams.kategorie ||
            prevParamsRef.current.merkmal !== currentParams.merkmal ||
            prevParamsRef.current.kennzeichen !== currentParams.kennzeichen ||
            prevParamsRef.current.serienBezeichnung !== currentParams.serienBezeichnung ||
            prevParamsRef.current.status !== currentParams.status ||
            prevParamsRef.current.gewichtMin !== currentParams.gewichtMin ||
            prevParamsRef.current.gewichtMax !== currentParams.gewichtMax
        );

        if (relevantParamsChanged && !shouldRestore) {
            const listElement = document.querySelector('.stones-list-section');
            if (listElement) {
                window.scrollTo({ top: listElement.offsetTop - 80, behavior: 'instant' });
            }
        }

        if (paramsJustChanged) {
            prevParamsRef.current = currentParams;
        }
    }, [currentParams]);


    // --- Event Handlers ---

    // Helper to update URL search parameters
    const updateSearchParams = (newValues) => {
        const updated = new URLSearchParams(searchParams);
        let changed = false;
        let filtersChanged = false;

        for (const key in newValues) {
            const oldValue = updated.get(key) ?? defaultParams[key];
            const newValue = String(newValues[key] ?? '');
            const effectiveOldValue = String(oldValue ?? '');

            if (newValue !== effectiveOldValue) {
                const isFilterKey = !['page', 'limit', 'sortBy', 'sortOrder'].includes(key);

                if (newValue === '' || newValue === String(defaultParams[key] ?? '')) {
                    if (updated.has(key)) {
                        updated.delete(key);
                        changed = true;
                        if (isFilterKey) filtersChanged = true;
                    }
                } else {
                    updated.set(key, newValue);
                    changed = true;
                    if (isFilterKey) filtersChanged = true;
                }
            }
        }

        if (changed && filtersChanged && updated.get('page') !== '1') {
            updated.set('page', '1');
        } else if (changed && !filtersChanged && newValues.hasOwnProperty('limit') && updated.get('page') !== '1') {
            updated.set('page', '1');
        }

        if (changed) {
            sessionStorage.removeItem('restoreScroll');
            sessionStorage.removeItem('scrollPosition');
            isRestoringScroll.current = false;
            setSearchParams(updated, { replace: true });
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        updateSearchParams({ [name]: value });
    };

    const handleStatusFilterClick = (status) => {
        updateSearchParams({ status: currentParams.status === status ? '' : status });
    };

    const handleReset = () => {
        const resetParams = {};
        for (const key in defaultParams) {
            if (key !== 'limit') {
                resetParams[key] = defaultParams[key];
            }
        }
        resetParams.limit = currentParams.limit || defaultParams.limit;

        const resetSearchParams = new URLSearchParams();
        for (const key in resetParams) {
            if (resetParams[key] !== defaultParams[key]) {
                resetSearchParams.set(key, resetParams[key]);
            }
        }
        if (!resetSearchParams.has('page')) resetSearchParams.set('page', '1');
        if (!resetSearchParams.has('limit')) resetSearchParams.set('limit', defaultParams.limit);

        sessionStorage.removeItem('restoreScroll');
        sessionStorage.removeItem('scrollPosition');
        sessionStorage.removeItem(FILTER_STORAGE_KEY);
        isRestoringScroll.current = false;
        setSearchParams(resetSearchParams, { replace: true });
        setShowFilter(false);
    };


    // --- Navigation Handlers with Scroll Saving ---
    const saveScrollAndNavigate = (path) => {
        sessionStorage.setItem('scrollPosition', window.pageYOffset.toString());
        sessionStorage.setItem('restoreScroll', 'true');
        navigate(path);
    };

    const handleEdit = (e, id) => {
        e.stopPropagation(); // Prevent card click
        saveScrollAndNavigate(`/steine/${id}?edit=true`);
    };

     const handleCardClick = (event, stein) => {
        // Ensure click is not on interactive elements within the card
        if (event.target.closest('button, a, input, select, textarea, .bearbeiten-button, .loeschen-button, img')) {
            // Allow clicks on image (for modal), edit, delete buttons to proceed
            if (!event.target.closest('img')) { // If not image click, stop propagation here too
                 return;
            }
            // Let image click propagate to its own handler if necessary, but don't navigate card
            if (event.target.closest('button, a, input, select, textarea')) return;
        }
         saveScrollAndNavigate(`/steine/${stein._id}`);
    };

    const handleDelete = (e, id) => {
        e.stopPropagation(); // Prevent card click
        if (window.confirm('Möchten Sie diesen Stein wirklich löschen?')) {
             // No scroll saving needed here, just refetch
             // Clear restore flag in case it was somehow set
            sessionStorage.removeItem('restoreScroll');
            sessionStorage.removeItem('scrollPosition');
            isRestoringScroll.current = false;
            setIsLoading(true); // Show loading during delete/refetch
            API.delete(`/steine/${id}`)
                .then(() => {
                    // Fetch with current params to refresh the list
                    fetchSteine(currentParams);
                })
                .catch((err) => {
                    console.error("Fehler beim Löschen:", err);
                    setError('Fehler beim Löschen des Steins.');
                    setIsLoading(false); // Stop loading on error
                });
        }
    };

    const handleImageClick = (event, stein) => {
        event.stopPropagation();
        if (stein.bildPfad) {
            setSelectedImage(`${process.env.REACT_APP_BASE_URL}/${stein.bildPfad}`);
            setShowModal(true);
        }
    };

    // --- Sorting ---
    const handleSortChange = (e) => {
        const newSortBy = e.target.value;
        // Reset sort order to 'asc' if changing sortBy field, unless it was already selected
        const newSortOrder = newSortBy && newSortBy === currentParams.sortBy ? currentParams.sortOrder : 'asc';
        updateSearchParams({ sortBy: newSortBy, sortOrder: newSortOrder });
    };

    const toggleSortOrder = () => {
        // Only toggle if a sortBy field is selected
        if (currentParams.sortBy) {
            updateSearchParams({ sortOrder: currentParams.sortOrder === 'asc' ? 'desc' : 'asc' });
        }
    };

    // --- Pagination ---
    const itemsPerPage = parseInt(currentParams.limit, 10) || 0; // Treat 0 as "show all"
    const currentPage = parseInt(currentParams.page, 10) || 1;
    const gesamtSeiten = itemsPerPage === 0 ? 1 : Math.ceil(totalSteine / itemsPerPage);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= gesamtSeiten && newPage !== currentPage) {
             updateSearchParams({ page: newPage.toString() });
        }
    };

    const handleItemsPerPageChange = (e) => {
        const newLimit = e.target.value;
        // Reset page to 1 when changing items per page
        updateSearchParams({ limit: newLimit, page: '1' });
    };

    // --- Rendering ---
    const aktuelleSteine = useMemo(() => steine || [], [steine]);

    return (
        <main>
            {/* Search and Filter Section */}
            <section className="search-filter-section">
                {/* Search Bar */}
                 <div className="search-bar">
                     <input
                        type="text" id="search" name="search" placeholder="Suche nach Name oder Notizen..."
                        value={currentParams.search} onChange={handleFilterChange} aria-label="Suche nach Name oder Notizen"
                    />
                    {userRole !== 'guest' && (
                        <button className="toggle-filter-button" onClick={() => setShowFilter(!showFilter)} aria-expanded={showFilter}>
                            {showFilter ? 'Filter ausblenden' : 'Filter anzeigen'}
                        </button>
                    )}
                </div>
                 {/* Status Quick Filters */}
                <div className="status-filter-buttons">
                  {['', 'potenzieller Kauf', 'gekauft', 'verkauft', 'noch keinen Kontakt'].map(status => (
                      <button key={status || 'alle'}
                          className={`status-filter-button ${status.replace(/\s+/g, '-').toLowerCase() || 'all'} ${currentParams.status === status ? 'active' : ''}`}
                          onClick={() => handleStatusFilterClick(status)} > {status || 'Alle'} </button>
                  ))}
                </div>
                 {/* Advanced Filters Form (Conditional) */}
                 {userRole !== 'guest' && showFilter && (
                    <div className="filters">
                        <h2>Filteroptionen</h2>
                        {/* Form structure remains the same */}
                        <form onSubmit={(e) => e.preventDefault()}>
                            <fieldset className="filter-group"> <legend>Allgemein</legend>
                                <div className="filter-item">
                                    <label htmlFor="kategorie">Kategorie:</label>
                                    <select id="kategorie" name="kategorie" value={currentParams.kategorie} onChange={handleFilterChange}>
                                        <option value="">Alle Kategorien</option>
                                        {KATEGORIEN.map((k) => <option key={k} value={k}>{k}</option>)}
                                    </select>
                                </div>
                                <div className="filter-item">
                                    <label htmlFor="merkmal">Merkmal:</label>
                                    <select id="merkmal" name="merkmal" value={currentParams.merkmal} onChange={handleFilterChange}>
                                        <option value="">Alle Merkmale</option>
                                        {merkmalOptions.map((m) => <option key={m} value={m}>{m}</option>)}
                                    </select>
                                </div>
                                <div className="filter-item">
                                    <label htmlFor="farbe">Farbe:</label>
                                    <select id="farbe" name="farbe" value={currentParams.farbe} onChange={handleFilterChange}>
                                        <option value="">Alle Farben</option> {farbenOptions.map((f) => <option key={f} value={f}>{f}</option>)}
                                    </select>
                                </div>
                                <div className="filter-item">
                                    <label htmlFor="kennzeichen">Kennzeichen:</label>
                                    <input type="text" id="kennzeichen" name="kennzeichen" value={currentParams.kennzeichen} onChange={handleFilterChange} placeholder="z. B. TF-2026-0001" />
                                </div>
                                <div className="filter-item">
                                    <label htmlFor="serienBezeichnung">Serienbezeichnung:</label>
                                    <select id="serienBezeichnung" name="serienBezeichnung" value={currentParams.serienBezeichnung} onChange={handleFilterChange} disabled={currentParams.kategorie !== 'Serien'}>
                                        <option value="">Alle Serien</option> {serienBezeichnungOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </div>
                                <div className="filter-item">
                                  <label htmlFor="status-select">Status:</label>
                                  <select id="status-select" name="status" value={currentParams.status} onChange={handleFilterChange}>
                                    <option value="">Alle Status</option>
                                    <option value="potenzieller Kauf">Potenzieller Kauf</option>
                                    <option value="gekauft">Gekauft</option>
                                    <option value="verkauft">Verkauft</option>
                                    <option value="noch keinen Kontakt">Noch keinen Kontakt</option>
                                  </select>
                                </div>
                            </fieldset>
                            {/* Dimensions Filters */}
                            <fieldset className="filter-group"> <legend>Maße</legend>
                                <div className="filter-item"> <label htmlFor="gewichtMin">Gewicht von (g):</label> <input type="number" id="gewichtMin" name="gewichtMin" step="0.01" min="0" value={currentParams.gewichtMin} onChange={handleFilterChange} /> </div>
                                <div className="filter-item"> <label htmlFor="gewichtMax">Gewicht bis (g):</label> <input type="number" id="gewichtMax" name="gewichtMax" step="0.01" min="0" value={currentParams.gewichtMax} onChange={handleFilterChange} /> </div>
                                <div className="filter-item"> <label htmlFor="durchmesserMin">Durchm. von (mm):</label> <input type="number" id="durchmesserMin" name="durchmesserMin" step="0.01" min="0" value={currentParams.durchmesserMin} onChange={handleFilterChange} /> </div>
                                <div className="filter-item"> <label htmlFor="durchmesserMax">Durchm. bis (mm):</label> <input type="number" id="durchmesserMax" name="durchmesserMax" step="0.01" min="0" value={currentParams.durchmesserMax} onChange={handleFilterChange} /> </div>
                                <div className="filter-item"> <label htmlFor="laengeMin">Länge von (mm):</label> <input type="number" id="laengeMin" name="laengeMin" step="0.01" min="0" value={currentParams.laengeMin} onChange={handleFilterChange} /> </div>
                                <div className="filter-item"> <label htmlFor="laengeMax">Länge bis (mm):</label> <input type="number" id="laengeMax" name="laengeMax" step="0.01" min="0" value={currentParams.laengeMax} onChange={handleFilterChange} /> </div>
                                <div className="filter-item"> <label htmlFor="breiteMin">Breite von (mm):</label> <input type="number" id="breiteMin" name="breiteMin" step="0.01" min="0" value={currentParams.breiteMin} onChange={handleFilterChange} /> </div>
                                <div className="filter-item"> <label htmlFor="breiteMax">Breite bis (mm):</label> <input type="number" id="breiteMax" name="breiteMax" step="0.01" min="0" value={currentParams.breiteMax} onChange={handleFilterChange} /> </div>
                            </fieldset>
                             {/* Price Filters */}
                             <fieldset className="filter-group"> <legend>Preis</legend>
                                <div className="filter-item"> <label htmlFor="preisProGrammMin">Preis/g von (€):</label> <input type="number" id="preisProGrammMin" name="preisProGrammMin" step="0.01" min="0" value={currentParams.preisProGrammMin} onChange={handleFilterChange} /> </div>
                                <div className="filter-item"> <label htmlFor="preisProGrammMax">Preis/g bis (€):</label> <input type="number" id="preisProGrammMax" name="preisProGrammMax" step="0.01" min="0" value={currentParams.preisProGrammMax} onChange={handleFilterChange} /> </div>
                                <div className="filter-item"> <label htmlFor="kaufPreisMin">Kaufpreis von (€):</label> <input type="number" id="kaufPreisMin" name="kaufPreisMin" step="0.01" min="0" value={currentParams.kaufPreisMin} onChange={handleFilterChange} /> </div>
                                <div className="filter-item"> <label htmlFor="kaufPreisMax">Kaufpreis bis (€):</label> <input type="number" id="kaufPreisMax" name="kaufPreisMax" step="0.01" min="0" value={currentParams.kaufPreisMax} onChange={handleFilterChange} /> </div>
                                <div className="filter-item"> <label htmlFor="verkaufPreisMin">Verkaufspreis von (€):</label> <input type="number" id="verkaufPreisMin" name="verkaufPreisMin" step="0.01" min="0" value={currentParams.verkaufPreisMin} onChange={handleFilterChange} /> </div>
                                <div className="filter-item"> <label htmlFor="verkaufPreisMax">Verkaufspreis bis (€):</label> <input type="number" id="verkaufPreisMax" name="verkaufPreisMax" step="0.01" min="0" value={currentParams.verkaufPreisMax} onChange={handleFilterChange} /> </div>
                            </fieldset>
                            {/* Date Filters */}
                            <fieldset className="filter-group"> <legend>Datum</legend>
                                <div className="filter-item"> <label htmlFor="kaufDatumVon">Kaufdatum von:</label> <input type="date" id="kaufDatumVon" name="kaufDatumVon" value={currentParams.kaufDatumVon} onChange={handleFilterChange} /> </div>
                                <div className="filter-item"> <label htmlFor="kaufDatumBis">Kaufdatum bis:</label> <input type="date" id="kaufDatumBis" name="kaufDatumBis" value={currentParams.kaufDatumBis} onChange={handleFilterChange} /> </div>
                                <div className="filter-item"> <label htmlFor="verkaufDatumVon">Verkaufdatum von:</label> <input type="date" id="verkaufDatumVon" name="verkaufDatumVon" value={currentParams.verkaufDatumVon} onChange={handleFilterChange} /> </div>
                                <div className="filter-item"> <label htmlFor="verkaufDatumBis">Verkaufdatum bis:</label> <input type="date" id="verkaufDatumBis" name="verkaufDatumBis" value={currentParams.verkaufDatumBis} onChange={handleFilterChange} /> </div>
                            </fieldset>
                            {/* Reset Button */}
                            <div className="filter-buttons"> <button type="button" onClick={handleReset} className="btn btn-reset"> Filter zurücksetzen </button> </div>
                        </form>
                    </div>
                )}
            </section>

            <hr className="section-divider" />

            {/* Sorting Controls */}
            <div className="sort-controls">
                <label htmlFor="sortBy">Sortieren nach:</label>
                <select id="sortBy" value={currentParams.sortBy} onChange={handleSortChange}>
                    {/* Options remain the same */}
                     <option value="name">Name</option>
                     <option value="kennzeichen">Kennzeichen</option>
                     <option value="kategorie">Kategorie</option>
                     <option value="merkmal">Merkmal</option>
                     <option value="status">Status</option>
                     <option value="gewicht">Gewicht</option>
                     <option value="preisProGramm">Preis pro Gramm</option>
                     <option value="kaufPreis">Kaufpreis</option>
                     <option value="verkaufPreis">Verkaufspreis</option>
                     <option value="laenge">Länge</option>
                     <option value="breite">Breite</option>
                     <option value="tiefe">Tiefe</option>
                     <option value="dicke">Dicke</option>
                     <option value="kaufDatum">Kaufdatum</option>
                     <option value="verkaufDatum">Verkaufsdatum</option>
                </select>
                <button onClick={toggleSortOrder} className={`sort-order-button ${currentParams.sortOrder === 'asc' ? 'asc' : 'desc'}`} disabled={!currentParams.sortBy} aria-label={`Sortierrichtung umschalten, aktuell ${currentParams.sortOrder === 'asc' ? 'aufsteigend' : 'absteigend'}`} >
                    {currentParams.sortOrder === 'asc' ? (<>Auf <FontAwesomeIcon icon={faSortUp} /></>) : (<>Ab <FontAwesomeIcon icon={faSortDown} /></>)}
                </button>
            </div>

            <hr className="section-divider" />

             {/* Stones List Section - Add ID for easier targeting */}
            <section id="stein-liste-anker" className="stones-list-section">
                <div className="stones-header">
                    {userRole === 'admin' && ( <Link to="/steine/new" className="btn btn-add"> Neuen Stein hinzufügen </Link> )}
                    {/* Info Display */}
                    <div className="stones-count">
                       {isLoading ? ( <p>Lade Steine...</p> ) : error ? null : // Show loading text here or below
                         ( itemsPerPage === 0 ? ( <p>Zeige alle {totalSteine} Steine</p> ) : ( <p> Zeige {aktuelleSteine.length} von {totalSteine} Steinen {gesamtSeiten > 1 && ` (Seite ${currentPage} von ${gesamtSeiten})`} </p> ) )
                       }
                    </div>
                </div>

                {/* Loading / Error / No Results States */}
                 {isLoading && !error && ( <div className="loading-indicator"><p>Lade Steine...</p></div> )}
                 {!isLoading && error && (
                    <div className="error-message"> <p>{error}</p>
                        {!error.includes("anmelden") && !error.includes("Sitzung") && <button onClick={() => fetchSteine(currentParams)} className="btn btn-retry"> Erneut versuchen </button> }
                    </div>
                 )}
                 {!isLoading && !error && aktuelleSteine.length === 0 && ( <p>Keine Steine gefunden, die den Kriterien entsprechen.</p> )}

                 {/* Grid (only render if not loading, no error, and stones exist) */}
                 {!isLoading && !error && aktuelleSteine.length > 0 && (
                    <>
                        <div className="stein-grid">
                            {aktuelleSteine.map((stein) => (
                                <div key={stein._id} className={`stein-card status-${stein.status ? stein.status.replace(/\s+/g, '-').toLowerCase() : 'unbekannt'}`}
                                    onClick={(e) => handleCardClick(e, stein)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleCardClick(e, stein); }} aria-label={`Details für ${stein.name || 'Unbenannt'} anzeigen`} >
                                    {/* Card content remains the same */}
                                     <SteinBild
                                        bildPfad={stein.bildPfad}
                                        size="card"
                                        onClick={(e) => handleImageClick(e, stein)}
                                        alt={stein.name || 'Steinbild'}
                                    />
                                    <div className="stein-details">
                                        <h3 className="stein-name">{stein.name || 'Unbenannt'}</h3>
                                        {stein.kennzeichen && <p className="stein-property"><span className="property-label kennzeichen-label">Kz.:</span> <span className="kennzeichen-badge-small">{stein.kennzeichen}</span></p>}
                                        {stein.kategorie && <p className="stein-property"><span className="property-label">Kat.:</span> {stein.kategorie}</p>}
                                        {stein.merkmal && <p className="stein-property"><span className="property-label">Merkmal:</span> {stein.merkmal}</p>}
                                        {stein.status && <p className="stein-property"><span className="property-label">Status:</span> {stein.status}</p>}
                                        {stein.gewicht != null && <p className="stein-property"><span className="property-label">Gewicht:</span> {stein.gewicht} g</p>}
                                        {stein.laenge != null && <p className="stein-property"><span className="property-label">Länge:</span> {stein.laenge} mm</p>}
                                        {stein.breite != null && <p className="stein-property"><span className="property-label">Breite:</span> {stein.breite} mm</p>}
                                        {stein.tiefe != null && <p className="stein-property"><span className="property-label">Tiefe:</span> {stein.tiefe} mm</p>}
                                        {stein.aufGlas && <p className="stein-property"><span className="property-label">Montierung:</span> Auf Glas</p>}
                                        {!stein.aufGlas && stein.dicke != null && <p className="stein-property"><span className="property-label">Dicke:</span> {stein.dicke} mm</p>}
                                        {stein.durchmesser != null && <p className="stein-property"><span className="property-label">Durchm.:</span> {stein.durchmesser} mm</p>}
                                        {stein.preisProGramm != null && <p className="stein-property"><span className="property-label">€/g:</span> {stein.preisProGramm}</p>}
                                        {stein.kaufPreis != null && <p className="stein-property"><span className="property-label">Kauf:</span> € {stein.kaufPreis}</p>}
                                        {stein.verkaufPreis != null && <p className="stein-property"><span className="property-label">Verkauf:</span> € {stein.verkaufPreis}</p>}
                                        {stein.serienBezeichnung && <p className="stein-property"><span className="property-label">Serie:</span> {stein.serienBezeichnung}</p>}
                                    </div>
                                    {userRole === 'admin' && (
                                        <div className="button-group">
                                            <button onClick={(e) => handleEdit(e, stein._id)} className="bearbeiten-button" title="Stein bearbeiten"> <FontAwesomeIcon icon={faEdit} /> <span className="button-text">Bearb.</span> </button>
                                            <button onClick={(e) => handleDelete(e, stein._id)} className="loeschen-button" title="Stein löschen"> <FontAwesomeIcon icon={faTrash} /> <span className="button-text">Lösch.</span> </button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                         {/* Pagination Controls */}
                         {gesamtSeiten > 1 && itemsPerPage !== 0 && (
                            <div className="pagination">
                                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1 || isLoading} className="pagination-button"> <FontAwesomeIcon icon={faChevronLeft} /> Zurück </button>
                                <span className="pagination-info" aria-live="polite" aria-atomic="true"> Seite {currentPage} von {gesamtSeiten} </span>
                                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === gesamtSeiten || isLoading} className="pagination-button"> Weiter <FontAwesomeIcon icon={faChevronRight} /> </button>
                            </div>
                        )}

                        {/* Items per Page Selector */}
                        <div className="items-per-page">
                            <label htmlFor="itemsPerPage">Steine pro Seite:</label>
                            <select id="itemsPerPage" value={currentParams.limit} /* Bind to currentParams.limit */ onChange={handleItemsPerPageChange} disabled={isLoading} >
                                <option value="6">6</option> <option value="12">12</option> <option value="24">24</option> <option value="48">48</option> <option value="96">96</option> <option value="0">Alle</option> {/* Use '0' consistently */}
                            </select>
                        </div>
                    </>
                 )}
            </section>

            {/* Modal for Image */}
            {showModal && ( <Modal onClose={() => setShowModal(false)}> <img src={selectedImage} alt="Stein Großansicht" className="modal-image" /> </Modal> )}
        </main>
    );
}

export default SteinListe;